/**
 * Identidade deterministica: slug, iniciais, hash e o UUID da URL de webhook.
 *
 * Decisao nao obvia: a URL de webhook de cada empresa leva um UUID DERIVADO
 * do id (FNV-1a em quatro blocos, com os nibbles de versao/variante de um v4),
 * e nao o id legivel. E o que o backend vai fazer com `companies.id`; na
 * previa, derivar do texto garante que a mesma empresa mostra sempre a mesma
 * URL em qualquer maquina -- sem Math.random e sem `crypto.randomUUID`, que
 * mudaria a cada carregamento e faria a pessoa achar que a URL "trocou".
 */

import type { ProvedorIntegracao } from './types';

/** Host publico do motor. Valor, nao contrato: por isso fica fora de types.ts. */
export const HOST_WEBHOOK = 'https://app.convertefacil.com.br';

/** Hash pequeno e estavel do texto: mesmo texto, mesmo numero, em qualquer maquina. */
export function hashSimulado(texto: string): number {
  let h = 7;
  for (let i = 0; i < texto.length; i += 1) {
    h = (h * 31 + texto.charCodeAt(i)) % 10_007;
  }
  return h;
}

function fnv1a32(texto: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < texto.length; i += 1) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** UUID com a cara de um v4 (8-4-4-4-12), derivado do texto. */
export function uuidDeterministico(seed: string): string {
  const blocos: string[] = [];
  for (let bloco = 0; bloco < 4; bloco += 1) {
    blocos.push(fnv1a32(`${seed}#${bloco}`).toString(16).padStart(8, '0'));
  }
  const s = blocos.join('');
  const variantes = ['8', '9', 'a', 'b'];
  const variante = variantes[parseInt(s.charAt(16), 16) % 4] ?? '8';
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-4${s.slice(13, 16)}-${variante}${s.slice(17, 20)}-${s.slice(20, 32)}`;
}

export function urlDoWebhook(provider: ProvedorIntegracao, companyId: string): string {
  return `${HOST_WEBHOOK}/webhooks/${provider}/${uuidDeterministico(companyId)}`;
}

/** "Escola Fluente" -> "escola-fluente". Sem acento, sem espaco, sem simbolo. */
export function slugDe(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Mesma regra de `CompanyProvider`: primeira letra do primeiro e do ultimo nome. */
export function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter((p) => p.length > 0);
  const primeira = partes[0]?.charAt(0) ?? 'C';
  const segunda = partes.length > 1 ? partes[partes.length - 1]?.charAt(0) ?? '' : partes[0]?.charAt(1) ?? '';
  return `${primeira}${segunda}`.toUpperCase();
}
